import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext.jsx";
import AuthLayout from "../components/AuthLayout.jsx";
import PasswordInput from "../components/PasswordInput.jsx";
import { FarmLoaderOverlay } from "../components/FarmLoader.jsx";
import Seo from "../components/Seo.jsx";

export default function Login() {
  const { login, requestLoginOtp, loginWithOtp } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // "password" for existing password accounts, "code" for the passwordless
  // flow signup now creates — most visitors land in "code".
  const [mode, setMode] = useState("code");
  const [form, setForm] = useState({ email: "", password: "" });
  const [otp, setOtp] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const goToAccount = () => navigate(searchParams.get("next") || "/account");

  const submitPassword = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await login(form);
    setBusy(false);
    // `next` carries the page (e.g. /checkout) the customer was sent here
    // from so signing in drops them right back where they left off.
    if (res.ok) goToAccount();
    else setError(res.error);
  };

  const sendCode = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setBusy(true);
    const res = await requestLoginOtp(form.email);
    setBusy(false);
    if (res.ok) {
      setCodeSent(true);
      setInfo(`We sent a 6-digit code to ${form.email}. It expires in ${res.ttlMinutes || 10} minutes.`);
    } else {
      setError(res.error);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otp.trim())) {
      setError("Enter the 6-digit code from your email.");
      return;
    }
    setError("");
    setBusy(true);
    const res = await loginWithOtp(form.email, otp.trim());
    setBusy(false);
    if (res.ok) goToAccount();
    else setError(res.error);
  };

  const switchMode = (next) => {
    setMode(next);
    setCodeSent(false);
    setOtp("");
    setError("");
    setInfo("");
  };

  return (
    <AuthLayout
      eyebrow="Welcome back"
      title="Sign in to your account"
      subtitle="Track orders, save addresses, and check out faster."
      aside={{ heading: "Good to see you again" }}
    >
      <Seo title="Sign in" path="/login" noindex />
      <FarmLoaderOverlay show={busy} message="Signing you in…" />

      {mode === "code" && !codeSent && (
        <form className="auth__form" onSubmit={sendCode}>
          {error && <p className="auth__error">{error}</p>}
          <label>
            Email
            <input type="email" name="email" value={form.email} onChange={onChange} required placeholder="you@example.com" />
          </label>
          <button type="submit" className="btn btn--primary btn--block btn--lg" disabled={busy}>
            {busy ? "Sending code…" : "Email me a code"}
          </button>
        </form>
      )}

      {mode === "code" && codeSent && (
        <form className="auth__form" onSubmit={verifyCode}>
          {error && <p className="auth__error">{error}</p>}
          {info && <p className="auth__banner">{info}</p>}
          <label>
            Verification code
            <input
              name="otp"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              placeholder="6-digit code"
              maxLength={6}
            />
          </label>
          <button type="submit" className="btn btn--primary btn--block btn--lg" disabled={busy}>
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <p className="auth__switch">
            Didn't get it?{" "}
            <button type="button" className="auth__linklike" onClick={sendCode} disabled={busy}>
              Resend code
            </button>
          </p>
        </form>
      )}

      {mode === "password" && (
        <form className="auth__form" onSubmit={submitPassword}>
          {error && <p className="auth__error">{error}</p>}
          <label>
            Email
            <input type="email" name="email" value={form.email} onChange={onChange} required placeholder="you@example.com" />
          </label>
          <PasswordInput value={form.password} onChange={onChange} required placeholder="••••••••" />
          <button type="submit" className="btn btn--primary btn--block btn--lg" disabled={busy}>
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
      )}

      <p className="auth__switch">
        {mode === "code" ? (
          <button type="button" className="auth__linklike" onClick={() => switchMode("password")} disabled={busy}>
            Have a password instead? Sign in with it
          </button>
        ) : (
          <button type="button" className="auth__linklike" onClick={() => switchMode("code")} disabled={busy}>
            Sign in with an emailed code instead
          </button>
        )}
      </p>
      <p className="auth__switch">
        New to Rimmon Farm?{" "}
        <Link to={searchParams.get("next") ? `/signup?next=${encodeURIComponent(searchParams.get("next"))}` : "/signup"}>
          Create an account
        </Link>
      </p>
    </AuthLayout>
  );
}
